import { useState, useEffect } from 'react';
import { 
  Building2, Clock, CheckCircle2, XCircle, Eye, Search, 
  Filter, RefreshCw, MapPin, Phone, Mail, Globe, FileText,
  Calendar, User, ChevronDown, AlertCircle, MailCheck, MailX, Send, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { api } from '@/App';

export default function PropertyRegistrations() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);

  // Resend verification email
  const handleResendVerification = async (email) => {
    try {
      setResendingEmail(true);
      const response = await api.post('/portal/resend-verification', { email });
      toast.success(response.data.message || 'Verification email sent!');
      if (response.data.demo_mode) {
        toast.info(`Demo mode: Verification token is ${response.data.verification_token}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification email');
    } finally {
      setResendingEmail(false);
    }
  };

  // Manually verify email (Super Admin only)
  const handleManualVerifyEmail = async (email) => {
    try {
      setResendingEmail(true);
      const response = await api.post('/portal/admin/manual-verify-email', { email });
      toast.success(response.data.message || 'Email verified successfully!');
      // Refresh registrations list
      fetchRegistrations();
      // Update selected registration if open
      if (selectedRegistration && selectedRegistration.email === email) {
        setSelectedRegistration({ ...selectedRegistration, email_verified: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify email');
    } finally {
      setResendingEmail(false);
    }
  };

  useEffect(() => {
    fetchRegistrations();
    fetchStats();
  }, [filter]);

  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await api.get(`/admin/property-registrations${params}`);
      setRegistrations(response.data);
    } catch (error) {
      toast.error('Failed to fetch registrations');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/property-registrations/stats/summary');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const handleApprove = async () => {
    if (!selectedRegistration) return;
    
    setActionLoading(true);
    try {
      await api.post(`/admin/property-registrations/${selectedRegistration.id}/approve`, {
        notes: approvalNotes
      });
      toast.success('Registration approved! Company and admin user created.');
      setShowApproveDialog(false);
      setShowDetailsDialog(false);
      setApprovalNotes('');
      fetchRegistrations();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRegistration || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setActionLoading(true);
    try {
      await api.post(`/admin/property-registrations/${selectedRegistration.id}/reject`, {
        reason: rejectionReason
      });
      toast.success('Registration rejected');
      setShowRejectDialog(false);
      setShowDetailsDialog(false);
      setRejectionReason('');
      fetchRegistrations();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredRegistrations = registrations.filter(reg => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      reg.company_name?.toLowerCase().includes(searchLower) ||
      reg.owner_name?.toLowerCase().includes(searchLower) ||
      reg.email?.toLowerCase().includes(searchLower) ||
      reg.city?.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100"><CheckCircle2 className="h-3 w-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><XCircle className="h-3 w-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="h-6 w-6 text-blue-600" />
            Property Registrations
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage property owner registration requests
          </p>
        </div>
        <Button onClick={() => { fetchRegistrations(); fetchStats(); }} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card 
          className={`cursor-pointer transition-all ${filter === 'all' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setFilter('all')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${filter === 'pending' ? 'ring-2 ring-yellow-500' : ''}`}
          onClick={() => setFilter('pending')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <div className="h-10 w-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${filter === 'approved' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setFilter('approved')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Approved</p>
                <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
              </div>
              <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all ${filter === 'rejected' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setFilter('rejected')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Rejected</p>
                <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
              </div>
              <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by company, name, email, or city..."
            className="pl-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-registrations"
          />
        </div>
      </div>

      {/* Registrations List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-gray-500 mt-2">Loading registrations...</p>
            </div>
          ) : filteredRegistrations.length === 0 ? (
            <div className="p-8 text-center">
              <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No registrations found</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredRegistrations.map((reg) => (
                <div 
                  key={reg.id}
                  className="p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {reg.company_name}
                        </h3>
                        {getStatusBadge(reg.status)}
                        {/* Email Verification Badge with Resend Option */}
                        {reg.email_verified ? (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs">
                            <MailCheck className="h-3 w-3 mr-1" /> Email Verified
                          </Badge>
                        ) : (
                          <div className="flex items-center gap-1">
                            <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 text-xs">
                              <MailX className="h-3 w-3 mr-1" /> Email Not Verified
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResendVerification(reg.email);
                              }}
                              disabled={resendingEmail}
                              title="Resend verification email"
                            >
                              {resendingEmail ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Send className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" /> {reg.owner_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" /> {reg.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {reg.city}, {reg.state}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> Applied: {formatDate(reg.created_at)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedRegistration(reg);
                          setShowDetailsDialog(true);
                        }}
                        data-testid={`view-${reg.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" /> View
                      </Button>
                      {reg.status === 'pending' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm">
                              Actions <ChevronDown className="h-4 w-4 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedRegistration(reg);
                                setShowApproveDialog(true);
                              }}
                              className="text-green-600"
                            >
                              <CheckCircle2 className="h-4 w-4 mr-2" /> Approve
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedRegistration(reg);
                                setShowRejectDialog(true);
                              }}
                              className="text-red-600"
                            >
                              <XCircle className="h-4 w-4 mr-2" /> Reject
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Registration Details
            </DialogTitle>
            <DialogDescription>
              ID: {selectedRegistration?.id}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRegistration && (
            <div className="space-y-6">
              {/* Status Banner */}
              <div className="flex gap-2 flex-wrap">
                <div className={`
                  px-3 py-2 rounded-lg flex items-center gap-2 flex-1
                  ${selectedRegistration.status === 'pending' ? 'bg-yellow-50 text-yellow-800' : ''}
                  ${selectedRegistration.status === 'approved' ? 'bg-green-50 text-green-800' : ''}
                  ${selectedRegistration.status === 'rejected' ? 'bg-red-50 text-red-800' : ''}
                `}>
                  {selectedRegistration.status === 'pending' && <Clock className="h-5 w-5" />}
                  {selectedRegistration.status === 'approved' && <CheckCircle2 className="h-5 w-5" />}
                  {selectedRegistration.status === 'rejected' && <XCircle className="h-5 w-5" />}
                  <span className="font-medium capitalize">{selectedRegistration.status}</span>
                  {selectedRegistration.status === 'rejected' && selectedRegistration.rejection_reason && (
                    <span className="text-sm ml-2">- {selectedRegistration.rejection_reason}</span>
                  )}
                </div>
                {/* Email Verification Status */}
                <div className={`
                  px-3 py-2 rounded-lg flex items-center gap-2
                  ${selectedRegistration.email_verified ? 'bg-emerald-50 text-emerald-800' : 'bg-orange-50 text-orange-800'}
                `}>
                  {selectedRegistration.email_verified ? (
                    <><MailCheck className="h-5 w-5" /> Email Verified</>
                  ) : (
                    <>
                      <MailX className="h-5 w-5" /> Email Not Verified
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 ml-2 text-orange-700 hover:text-orange-800 hover:bg-orange-100"
                        onClick={() => handleResendVerification(selectedRegistration.email)}
                        disabled={resendingEmail}
                        title="Resend verification email"
                      >
                        {resendingEmail ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <Send className="h-4 w-4 mr-1" />
                        )}
                        Resend
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Warning for unverified email - show for ALL statuses */}
              {!selectedRegistration.email_verified && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <AlertCircle className="h-4 w-4 inline mr-1" />
                      <strong>Note:</strong> This applicant has not verified their email address yet.
                      {selectedRegistration.status === 'approved' && (
                        <span className="ml-1 text-orange-600">(Property is approved but email unverified)</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResendVerification(selectedRegistration.email)}
                        disabled={resendingEmail}
                        className="border-orange-300 text-orange-700 hover:bg-orange-100"
                      >
                        {resendingEmail ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <Send className="h-4 w-4 mr-1" />
                        )}
                        Resend Email
                      </Button>
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => handleManualVerifyEmail(selectedRegistration.email)}
                        disabled={resendingEmail}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        <MailCheck className="h-4 w-4 mr-1" />
                        Verify Manually
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Owner Details */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">Owner Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span>{selectedRegistration.owner_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span>{selectedRegistration.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span>{selectedRegistration.phone}</span>
                  </div>
                </div>
              </div>

              {/* Company Details */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">Company Details</h4>
                <div className="space-y-2">
                  <p className="font-semibold text-lg">{selectedRegistration.company_name}</p>
                  <div className="flex items-start gap-2 text-sm text-gray-600">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                    <span>
                      {selectedRegistration.company_address}<br />
                      {selectedRegistration.city}, {selectedRegistration.state} - {selectedRegistration.pincode}
                    </span>
                  </div>
                  {selectedRegistration.website && (
                    <div className="flex items-center gap-2 text-sm">
                      <Globe className="h-4 w-4 text-gray-400" />
                      <a href={selectedRegistration.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {selectedRegistration.website}
                      </a>
                    </div>
                  )}
                  <div className="flex gap-4 text-sm">
                    {selectedRegistration.gst_number && (
                      <div className="flex items-center gap-1">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span>GST: {selectedRegistration.gst_number}</span>
                      </div>
                    )}
                    {selectedRegistration.pan_number && (
                      <div className="flex items-center gap-1">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span>PAN: {selectedRegistration.pan_number}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Property Details */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">Property Details</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Property Types:</span>
                    <div className="flex gap-1 flex-wrap">
                      {selectedRegistration.property_types?.map(type => (
                        <Badge key={type} variant="secondary" className="capitalize">{type}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600">Estimated Properties:</span>
                    <span className="font-medium">{selectedRegistration.estimated_properties}</span>
                  </div>
                  {selectedRegistration.description && (
                    <div className="mt-2">
                      <span className="text-sm text-gray-600">Description:</span>
                      <p className="text-sm mt-1 bg-gray-50 p-2 rounded">{selectedRegistration.description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">Timeline</h4>
                <div className="text-sm space-y-1">
                  <p><span className="text-gray-500">Applied:</span> {formatDate(selectedRegistration.created_at)}</p>
                  {selectedRegistration.approved_at && (
                    <p><span className="text-gray-500">Approved:</span> {formatDate(selectedRegistration.approved_at)} by {selectedRegistration.approved_by}</p>
                  )}
                  {selectedRegistration.rejected_at && (
                    <p><span className="text-gray-500">Rejected:</span> {formatDate(selectedRegistration.rejected_at)} by {selectedRegistration.rejected_by}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            {selectedRegistration?.status === 'pending' && (
              <div className="flex gap-2 w-full">
                <Button
                  variant="outline"
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => {
                    setShowDetailsDialog(false);
                    setShowRejectDialog(true);
                  }}
                >
                  <XCircle className="h-4 w-4 mr-2" /> Reject
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={() => {
                    setShowDetailsDialog(false);
                    setShowApproveDialog(true);
                  }}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" /> Approve
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-5 w-5" />
              Approve Registration
            </DialogTitle>
            <DialogDescription>
              This will create a new company and admin user account.
            </DialogDescription>
          </DialogHeader>
          
          {selectedRegistration && (
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="font-semibold">{selectedRegistration.company_name}</p>
                <p className="text-sm text-gray-600">{selectedRegistration.owner_name} • {selectedRegistration.email}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium">Approval Notes (Optional)</label>
                <Textarea
                  placeholder="Add any notes about this approval..."
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                The owner will be able to login with their email and password to manage their properties.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={handleApprove}
              disabled={actionLoading}
            >
              {actionLoading ? 'Approving...' : 'Confirm Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Reject Registration
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for rejection.
            </DialogDescription>
          </DialogHeader>
          
          {selectedRegistration && (
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="font-semibold">{selectedRegistration.company_name}</p>
                <p className="text-sm text-gray-600">{selectedRegistration.owner_name} • {selectedRegistration.email}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium">Rejection Reason *</label>
                <Textarea
                  placeholder="Explain why this registration is being rejected..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="mt-1"
                  required
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleReject}
              disabled={actionLoading || !rejectionReason.trim()}
            >
              {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
